import { useEffect } from "react";
import { useSearchParams } from "react-router";
import { Skeleton, Tabs } from "@omnabase/ui";
import { useSession } from "../../shell/store";
import { useSetParam, useWs } from "../../lib/nav";
import { useIntegrations } from "../../lib/integrations";
import { capsOf } from "../../lib/access";
import { useScrollReset } from "../../lib/scrollReset";
import { useSites } from "../../lib/sites";
import { useWebhooks } from "../../lib/webhooks";
import { openStateOf, selectedTab, tabToRemember, tabsInUse, type IntegrationTabId, type KindCounts } from "../../lib/integrationTabs";
import { GithubSettings } from "./GithubSettings";
import { EndpointSettings } from "./EndpointSettings";
import { ApiKeysSettings } from "./ApiKeysSettings";
import { WebhooksSettings } from "./WebhooksSettings";
import { WordPressSettings } from "./WordPressSettings";
import { IntegrationCards } from "./IntegrationCards";

/** Settings › Integrations (M5 round four; M14 round three: only what works, the same for everyone who opens the page). Integrations round
 *  three (the founder's orders of 2026-10-13): the kit's tabs with `?tab=`, as a project's settings: GitHub (`GithubSettings.tsx`), WordPress
 *  (`WordPressSettings.tsx`), Your own system (`EndpointSettings.tsx`), Webhooks (`WebhooksSettings.tsx`), API keys (`ApiKeysSettings.tsx`),
 *  each only while the kind is in use, then "Add new" (`IntegrationCards.tsx`): every kind as a card, and "Request an integration". The
 *  strip is drawn once the three reads it depends on have answered (no jump). Every open state stays in the URL (`?connect=github`,
 *  `?pick=…`, `?disconnect=…`, `?wordpress=…`, `?endpoint=…`, `?webhook=…`, `?deliveries=…`, `?apikey=…`, `?request=…`); each section's
 *  dialogs render whichever tab is shown, an open state selects its tab and the address remembers it (`lib/integrationTabs.ts`). Nothing of a
 *  site's (its key, its addresses, its endpoint) is listed under the developers' kinds; it lives on the WordPress tab. */
const TABS: { id: IntegrationTabId; label: string }[] = [
  { id: "github", label: "GitHub" },
  { id: "wordpress", label: "WordPress" },
  { id: "endpoints", label: "Your own system" },
  { id: "webhooks", label: "Webhooks" },
  { id: "api-keys", label: "API keys" },
  { id: "add", label: "Add new" },
];

export function IntegrationsSettings() {
  const { slug } = useWs();
  const { workspace } = useSession();
  const [params] = useSearchParams(); const setParam = useSetParam();
  const admin = capsOf(workspace).has("projectSettings");
  const data = useIntegrations(slug);
  const siteList = useSites(slug, admin); // the WordPress tab, the key table and the cards read the same rows
  const hookList = useWebhooks(slug, admin);
  const answered = (r: { data: unknown; error: string | null }) => !!(r.data || r.error);
  const ready = answered(data) && (!admin || (answered(siteList) && answered(hookList)));
  const rows = siteList.data?.rows ?? [], hooks = hookList.data?.webhooks ?? [], endpoints = data.data?.endpoints ?? [];
  const counts: KindCounts = { github: data.data?.github.length ?? 0, sites: rows.filter((s) => s.connected).length, endpoints: endpoints.filter((e) => !e.siteId).length, webhooks: hooks.filter((w) => !w.fromConnect).length, keys: rows.filter((s) => !s.connected).length };
  const shown = tabsInUse(TABS, counts);
  const facts = { connectedSites: new Set(rows.filter((s) => s.connected).map((s) => s.id)), siteWebhooks: new Set(hooks.filter((w) => w.fromConnect).map((w) => w.id)) };
  const asked = params.get("tab"), state = openStateOf(params, facts);
  const tab = selectedTab(asked, state, shown.map((t) => t.id));
  const remember = ready ? tabToRemember(asked, state, tab) : null;
  const top = useScrollReset(tab); // a tab starts at the top (polish round one)
  useEffect(() => { if (remember) setParam("tab", remember); }, [remember]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <section className="ob-profile" aria-label="Integrations">
      <header className="ob-profile__head">
        <div className="ob-settings__head"><div><h1 className="ob-page__title">Integrations</h1><p className="ob-page__subtitle">What this workspace is connected to, and the addresses and keys other systems use to reach it.</p></div></div>
        {ready && <Tabs variant="line" items={shown} active={tab} onChange={(id) => setParam("tab", id)} />}
      </header>
      <div className="ob-settings__scroll" ref={top}><div className={tab === "add" ? "ob-form ob-form--cards" : "ob-form"}>
        {!ready ? <div className="ob-skeleton-list" aria-busy="true" aria-label="Reading what this workspace is connected to">{[0, 1, 2].map((i) => <Skeleton key={i} shape="card" />)}</div> : <>
          <GithubSettings accounts={data.data?.github ?? null} shown={tab === "github"} reload={() => void data.reload()} />
          <WordPressSettings rows={siteList.data?.rows ?? null} endpoints={endpoints} plugin={data.data?.plugin ?? null} shown={tab === "wordpress"} reload={() => void siteList.reload()} reloadEndpoints={() => void data.reload()} />
          <EndpointSettings endpoints={endpoints.filter((e) => !e.siteId)} configured={data.data?.endpointsConfigured ?? true} shown={tab === "endpoints"} reload={() => void data.reload()} />
          <WebhooksSettings list={hookList} shown={tab === "webhooks"} />
          <ApiKeysSettings list={siteList} shown={tab === "api-keys"} />
          {tab === "add" && <IntegrationCards counts={counts} requests={data.data?.requests ?? false} endpointsConfigured={data.data?.endpointsConfigured ?? true} />}
        </>}
      </div></div>
    </section>
  );
}
